import {
  createApi,
  fetchBaseQuery,
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query/react";
import type { ThemeVariables } from "../types";

// This nonce is for authenticating with the WP REST API (for our custom endpoints)
let currentWpRestApiNonce: string | undefined = (
  window.themeVariables as ThemeVariables | undefined
)?.wpRestApiNonce;

const updateWpRestNonce = (newNonce: string | undefined) => {
  if (newNonce && newNonce !== currentWpRestApiNonce) {
    currentWpRestApiNonce = newNonce;
  }
};

const rawBaseQuery = fetchBaseQuery({
  baseUrl: (window.themeVariables as ThemeVariables | undefined)?.siteUrl || "",
  prepareHeaders: (headers) => {
    // Add the WP REST API Nonce header if it exists. This is used for our custom endpoints.
    if (currentWpRestApiNonce) {
      // Use the correct header name: "X-WP-Nonce"
      headers.set("X-WP-Nonce", currentWpRestApiNonce);
    }

    return headers;
  },
  credentials: "same-origin",
});

const customBaseQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const result = await rawBaseQuery(args, api, extraOptions);

  if (result.meta?.response?.headers) {
    const headers = result.meta.response.headers;

    // Update WP REST API Nonce (for our custom endpoints and general WP REST)
    const newWpRestNonce = headers.get("X-WP-Nonce");
    if (newWpRestNonce) {
      updateWpRestNonce(newWpRestNonce);
    }
  }

  if (
    result.error &&
    typeof result.error === "object" &&
    "status" in result.error
  ) {
    if (result.error.status === 401 || result.error.status === 403) {
      console.warn(
        `[API] Received ${result.error.status} error. Nonce may be expired.`
      );
    }
  }
  return result;
};

export const baseApi = createApi({
  reducerPath: "api",
  baseQuery: customBaseQuery,
  tagTypes: [
    "Products",
    "Variations",
    "Categories",
    "Tags",
    "Attributes",
    "Brands",
    "Cities",
    "CitiesWithDistricts",
  ],
  endpoints: () => ({}),
});

export const {
  util: { getRunningQueriesThunk },
  reducer: apiReducer,
  middleware: apiMiddleware,
} = baseApi;

// Export the updated nonce management function if needed elsewhere
export { updateWpRestNonce };
